<template>
  <div>
    <v-card class="elevation-0">
      <v-card-title class="flex-wrap">
        <span class="text-h5 text-md-h4 black--text font-weight-bold">Container Tags Management</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="fetchDockers" :loading="loading">
          <v-icon>mdi-sync</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-subtitle class="pb-0">
        <v-chip color="warning" small class="mr-2">
          <v-icon left small>mdi-alert</v-icon>
          Unreleased Tags Only
        </v-chip>
        <span class="text-caption d-none d-sm-inline">Manage tags that have not been released yet (released = false)</span>
      </v-card-subtitle>

      <v-card-text>
        <!-- Search and Bulk Actions -->
        <v-row class="mb-4" align="center">
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              outlined
              dense
              label="Search Containers..."
              append-icon="mdi-magnify"
              hide-details
              clearable
              v-model="dockerSearch"
              @input="onDockerSearchInput"
            ></v-text-field>
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="12" sm="6" md="auto" class="d-flex flex-wrap justify-end" v-if="selectedTags.length > 0">
            <v-btn color="success" small class="mr-2 mb-1" @click="releaseSelectedTags" :loading="actionLoading">
              <v-icon left small>mdi-check-circle</v-icon>
              <span class="d-none d-sm-inline">Release</span> ({{ selectedTags.length }})
            </v-btn>
            <v-btn color="error" small class="mb-1" @click="deleteSelectedTags" :loading="actionLoading">
              <v-icon left small>mdi-delete</v-icon>
              <span class="d-none d-sm-inline">Delete</span> ({{ selectedTags.length }})
            </v-btn>
          </v-col>
        </v-row>

        <!-- Expandable Docker List with Server-Side Pagination -->
        <v-data-table
          :headers="dockerHeaders"
          :items="dockers"
          :loading="loading"
          :expanded.sync="expandedDockers"
          item-key="id"
          show-expand
          single-expand
          class="elevation-1"
          :mobile-breakpoint="0"
          :server-items-length="dockersTotal"
          :options.sync="dockerOptions"
          :footer-props="{ 'items-per-page-options': [10, 20, 50] }"
          @item-expanded="onDockerExpanded"
        >
          <template v-slot:[`item.docker_name`]="{ item }">
            <div class="d-flex align-center">
              <v-icon left color="primary" small>mdi-cube-outline</v-icon>
              <span class="font-weight-medium text-truncate" style="max-width: 300px;">{{ item.docker_name }}</span>
            </div>
          </template>

          <template v-slot:[`item.unreleased_count`]="{ item }">
            <v-chip :color="item.unreleased_count > 10 ? 'error' : 'warning'" small>
              {{ item.unreleased_count }}
            </v-chip>
          </template>

          <template v-slot:[`item.docker_actions`]="{ item }">
            <div class="d-flex justify-center">
              <v-btn color="success" x-small class="mr-1" @click.stop="releaseAllForDocker(item)" :loading="actionLoading" title="Release All Unreleased Tags">
                <v-icon left x-small>mdi-check-circle</v-icon>
                Release All
              </v-btn>
              <v-btn color="error" x-small @click.stop="deleteAllForDocker(item)" :loading="actionLoading" title="Delete All Unreleased Tags">
                <v-icon left x-small>mdi-delete</v-icon>
                Delete All
              </v-btn>
            </div>
          </template>

          <!-- Expanded Row - Tags Table with Server-Side Pagination -->
          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="pa-2 pa-md-4 grey lighten-4">
              <v-card elevation="2">
                <v-card-title class="py-2 flex-wrap">
                  <span class="text-subtitle-2 text-md-subtitle-1">Unreleased Tags for {{ item.docker_name }}</span>
                  <v-spacer></v-spacer>
                  <v-text-field
                    outlined
                    dense
                    label="Search Tags..."
                    append-icon="mdi-magnify"
                    hide-details
                    clearable
                    v-model="tagSearch"
                    @input="onTagSearchInput"
                    class="mt-2 mt-md-0"
                    style="max-width: 250px; min-width: 150px;"
                  ></v-text-field>
                </v-card-title>
                <v-divider></v-divider>
                <v-data-table
                  :headers="tagHeaders"
                  :items="expandedTags"
                  :loading="tagsLoading"
                  show-select
                  v-model="selectedTags"
                  item-key="id"
                  dense
                  class="elevation-0"
                  :mobile-breakpoint="0"
                  :server-items-length="tagsTotal"
                  :options.sync="tagOptions"
                  :footer-props="{ 'items-per-page-options': [25, 50, 100] }"
                >
                  <template v-slot:[`item.name`]="{ item: tag }">
                    <span class="font-weight-medium text-truncate d-inline-block" style="max-width: 250px;" :title="tag.name">{{ tag.name }}</span>
                  </template>

                  <template v-slot:[`item.chipset_ids`]="{ item: tag }">
                    <div class="d-flex flex-wrap" style="max-width: 200px;">
                      <v-chip
                        class="mr-1 mb-1"
                        :key="index"
                        x-small
                        color="primary"
                        v-for="(chipset, index) in (tag.chipset_ids || []).slice(0, 3)"
                      >
                        {{ shortenChipset(chipset, allDevices) }}
                      </v-chip>
                      <v-chip v-if="tag.chipset_ids && tag.chipset_ids.length > 3" x-small color="grey">
                        +{{ tag.chipset_ids.length - 3 }}
                      </v-chip>
                    </div>
                    <v-icon v-if="!tag.chipset_ids || tag.chipset_ids.length === 0" small>mdi-minus</v-icon>
                  </template>

                  <template v-slot:[`item.actions`]="{ item: tag }">
                    <div class="d-flex justify-center">
                      <v-btn icon x-small class="mr-1" @click.stop="editTag(tag, item)" title="Edit">
                        <v-icon small>mdi-pencil</v-icon>
                      </v-btn>
                      <v-btn icon x-small color="success" class="mr-1" @click.stop="releaseSingleTag(tag)" title="Release">
                        <v-icon small>mdi-check</v-icon>
                      </v-btn>
                      <v-btn icon x-small color="error" @click.stop="deleteSingleTag(tag)" title="Delete">
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </div>
                  </template>
                </v-data-table>
              </v-card>
            </td>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Edit Tag Dialog -->
    <create-tag-view
      :tag="selectedTag"
      :dockerId="selectedDocker ? selectedDocker.id : null"
      :showCreateTagView="showEditTag"
      @close="closeEditTag"
      :allJetpacks="allJetpacks"
      @tag-updated="onTagUpdated"
    ></create-tag-view>
  </div>
</template>

<script>
import Swal from 'sweetalert2'
import { apiService } from '../service/api.service'
import CreateTagView from '../components/dockers/CreateTagView.vue'
import converter from '../mixins/converter'

export default {
  name: 'ContainerTags',
  components: { CreateTagView },
  // shortenChipset lives in the converter mixin now. This view used to carry its own copy that
  // returned the raw chipset id, so the same board was labelled differently here and in the
  // container dialog.
  mixins: [converter],
  data () {
    return {
      loading: false,
      tagsLoading: false,
      actionLoading: false,
      dockers: [],
      dockersTotal: 0,
      expandedDockers: [],
      expandedTags: [],
      tagsTotal: 0,
      selectedTags: [],
      dockerSearch: '',
      tagSearch: '',
      dockerSearchTimeout: null,
      tagSearchTimeout: null,
      expandedDockerId: null,
      skipTagWatch: false,
      allJetpacks: [],
      allDevices: [],
      showEditTag: false,
      selectedTag: null,
      selectedDocker: null,
      dockerOptions: {
        page: 1,
        itemsPerPage: 20
      },
      tagOptions: {
        page: 1,
        itemsPerPage: 25
      },
      dockerHeaders: [
        { text: '', value: 'data-table-expand', width: 40 },
        { text: 'Container', value: 'docker_name', width: 250 },
        { text: 'Unreleased Count', value: 'unreleased_count', align: 'center', width: 150 },
        { text: 'Actions', value: 'docker_actions', align: 'center', sortable: false, width: 220 }
      ],
      tagHeaders: [
        { text: 'Tag Name', value: 'name', width: 250 },
        { text: 'Devices', value: 'chipset_ids', sortable: false, width: 200 },
        { text: 'Actions', value: 'actions', align: 'center', sortable: false, width: 150 }
      ]
    }
  },
  watch: {
    dockerOptions: {
      handler () {
        this.fetchDockers()
      },
      deep: true
    },
    tagOptions: {
      handler () {
        if (this.skipTagWatch) return
        if (this.expandedDockerId) {
          this.fetchTagsForDocker(this.expandedDockerId)
        }
      },
      deep: true
    }
  },
  beforeMount () {
    this.fetchDockerSources()
  },
  methods: {
    onDockerSearchInput () {
      clearTimeout(this.dockerSearchTimeout)
      this.dockerSearchTimeout = setTimeout(() => {
        this.dockerOptions.page = 1
        this.fetchDockers()
      }, 400)
    },

    onTagSearchInput () {
      clearTimeout(this.tagSearchTimeout)
      this.tagSearchTimeout = setTimeout(() => {
        this.tagOptions.page = 1
        if (this.expandedDockerId) {
          this.fetchTagsForDocker(this.expandedDockerId)
        }
      }, 400)
    },

    async fetchDockers () {
      this.loading = true
      try {
        const { page, itemsPerPage } = this.dockerOptions
        const params = {
          page: page,
          per_page: itemsPerPage
        }
        if (this.dockerSearch) {
          params.search = this.dockerSearch
        }
        const res = await apiService.get('admin/unreleased-tags', { params })
        if (res.data.ok) {
          this.dockers = res.data.dockers
          this.dockersTotal = res.data.total
        }
      } catch (err) {
        console.error('Error fetching unreleased tags:', err)
        Swal.fire('Error', 'Failed to fetch unreleased tags', 'error')
      } finally {
        this.loading = false
      }
    },

    onDockerExpanded ({ item, value }) {
      if (value) {
        // Expanded
        this.expandedDockerId = item.id
        this.expandedTags = []
        this.tagsTotal = 0
        this.selectedTags = []
        this.tagSearch = ''
        this.skipTagWatch = true
        this.tagOptions = { page: 1, itemsPerPage: 25 }
        this.$nextTick(() => {
          this.skipTagWatch = false
          this.fetchTagsForDocker(item.id)
        })
      } else {
        // Collapsed
        this.expandedDockerId = null
        this.expandedTags = []
        this.tagsTotal = 0
        this.selectedTags = []
        this.tagSearch = ''
      }
    },

    async fetchTagsForDocker (dockerId) {
      this.tagsLoading = true
      try {
        const { page, itemsPerPage } = this.tagOptions
        const params = {
          page: page,
          per_page: itemsPerPage
        }
        if (this.tagSearch) {
          params.search = this.tagSearch
        }
        const res = await apiService.get(`admin/unreleased-tags/${dockerId}`, { params })
        if (res.data.ok) {
          this.expandedTags = res.data.tags
          this.tagsTotal = res.data.total
        }
      } catch (err) {
        console.error('Error fetching tags for docker:', err)
        Swal.fire('Error', 'Failed to fetch tags', 'error')
      } finally {
        this.tagsLoading = false
      }
    },

    async fetchDockerSources () {
      try {
        const res = await apiService.get('admin/get-docker-sources')
        this.allJetpacks = res.data.data.jetpacks || []
        // categories_all includes chipset 0, so Server-Workstation renders by name.
        this.allDevices = res.data.data.categories_all || res.data.data.categories || []
      } catch (err) {
        console.error('Error fetching docker sources:', err)
      }
    },

    editTag (tag, docker) {
      this.selectedTag = null
      this.selectedDocker = null

      this.$nextTick(() => {
        this.selectedTag = { ...tag }
        this.selectedDocker = docker
        this.showEditTag = true
      })
    },

    closeEditTag () {
      this.showEditTag = false
      this.$nextTick(() => {
        this.selectedTag = null
        this.selectedDocker = null
      })
    },

    onTagUpdated () {
      this.closeEditTag()
      this.fetchDockers()
      if (this.expandedDockerId) {
        this.fetchTagsForDocker(this.expandedDockerId)
      }
    },

    async releaseSingleTag (tag) {
      const result = await Swal.fire({
        title: 'Release Tag?',
        text: `Are you sure you want to release "${tag.name}"?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, release it!'
      })

      if (result.isConfirmed) {
        this.actionLoading = true
        try {
          await apiService.post('admin/release-tags', { tagIds: [tag.id] })
          Swal.fire('Released!', 'Tag has been released.', 'success')
          this.fetchDockers()
          if (this.expandedDockerId) {
            this.fetchTagsForDocker(this.expandedDockerId)
          }
        } catch (err) {
          console.error('Error releasing tag:', err)
          Swal.fire('Error', 'Failed to release tag', 'error')
        } finally {
          this.actionLoading = false
        }
      }
    },

    async deleteSingleTag (tag) {
      const result = await Swal.fire({
        title: 'Delete Tag?',
        text: `Are you sure you want to delete "${tag.name}"? This cannot be undone!`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!',
        confirmButtonColor: '#d33'
      })

      if (result.isConfirmed) {
        this.actionLoading = true
        try {
          await apiService.post('admin/delete-tags-bulk', { tagIds: [tag.id] })
          Swal.fire('Deleted!', 'Tag has been deleted.', 'success')
          this.fetchDockers()
          if (this.expandedDockerId) {
            this.fetchTagsForDocker(this.expandedDockerId)
          }
        } catch (err) {
          console.error('Error deleting tag:', err)
          Swal.fire('Error', 'Failed to delete tag', 'error')
        } finally {
          this.actionLoading = false
        }
      }
    },

    async releaseSelectedTags () {
      if (this.selectedTags.length === 0) return

      const result = await Swal.fire({
        title: 'Release Selected Tags?',
        text: `Are you sure you want to release ${this.selectedTags.length} tag(s)?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, release them!'
      })

      if (result.isConfirmed) {
        this.actionLoading = true
        try {
          const tagIds = this.selectedTags.map(t => t.id)
          await apiService.post('admin/release-tags', { tagIds })
          Swal.fire('Released!', `${this.selectedTags.length} tags have been released.`, 'success')
          this.selectedTags = []
          this.fetchDockers()
          if (this.expandedDockerId) {
            this.fetchTagsForDocker(this.expandedDockerId)
          }
        } catch (err) {
          console.error('Error releasing tags:', err)
          Swal.fire('Error', 'Failed to release tags', 'error')
        } finally {
          this.actionLoading = false
        }
      }
    },

    async releaseAllForDocker (docker) {
      const result = await Swal.fire({
        title: 'Release All Unreleased Tags?',
        text: `Are you sure you want to release ALL ${docker.unreleased_count} unreleased tag(s) for "${docker.docker_name}"?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, release all!'
      })

      if (result.isConfirmed) {
        this.actionLoading = true
        try {
          await apiService.post(`admin/release-all-tags/${docker.id}`)
          Swal.fire('Released!', `All unreleased tags for "${docker.docker_name}" have been released.`, 'success')
          this.fetchDockers()
          if (this.expandedDockerId === docker.id) {
            this.fetchTagsForDocker(docker.id)
          }
        } catch (err) {
          console.error('Error releasing all tags:', err)
          Swal.fire('Error', 'Failed to release all tags', 'error')
        } finally {
          this.actionLoading = false
        }
      }
    },

    async deleteAllForDocker (docker) {
      const result = await Swal.fire({
        title: 'Delete All Unreleased Tags?',
        text: `Are you sure you want to DELETE ALL ${docker.unreleased_count} unreleased tag(s) for "${docker.docker_name}"? This cannot be undone!`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete all!',
        confirmButtonColor: '#d33'
      })

      if (result.isConfirmed) {
        this.actionLoading = true
        try {
          await apiService.post(`admin/delete-all-unreleased-tags/${docker.id}`)
          Swal.fire('Deleted!', `All unreleased tags for "${docker.docker_name}" have been deleted.`, 'success')
          this.selectedTags = []
          this.fetchDockers()
          if (this.expandedDockerId === docker.id) {
            this.expandedDockerId = null
            this.expandedTags = []
            this.tagsTotal = 0
            this.expandedDockers = []
          }
        } catch (err) {
          console.error('Error deleting all tags:', err)
          Swal.fire('Error', 'Failed to delete all unreleased tags', 'error')
        } finally {
          this.actionLoading = false
        }
      }
    },

    async deleteSelectedTags () {
      if (this.selectedTags.length === 0) return

      const result = await Swal.fire({
        title: 'Delete Selected Tags?',
        text: `Are you sure you want to delete ${this.selectedTags.length} tag(s)? This cannot be undone!`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete them!',
        confirmButtonColor: '#d33'
      })

      if (result.isConfirmed) {
        this.actionLoading = true
        try {
          const tagIds = this.selectedTags.map(t => t.id)
          await apiService.post('admin/delete-tags-bulk', { tagIds })
          Swal.fire('Deleted!', `${this.selectedTags.length} tags have been deleted.`, 'success')
          this.selectedTags = []
          this.fetchDockers()
          if (this.expandedDockerId) {
            this.fetchTagsForDocker(this.expandedDockerId)
          }
        } catch (err) {
          console.error('Error deleting tags:', err)
          Swal.fire('Error', 'Failed to delete tags', 'error')
        } finally {
          this.actionLoading = false
        }
      }
    }
  }
}
</script>

<style scoped>
.font-weight-medium {
  font-weight: 500;
}

.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Responsive table adjustments */
@media (max-width: 600px) {
  .v-data-table >>> .v-data-table__wrapper {
    overflow-x: auto;
  }
}
</style>
